//Proofed

import { Box, Container, Grid, Typography, Stack } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { COLOR_PRIMARY, CHECKCIRCLE_SIZE, SECTION_LIGHT } from '../theme'
import { type StatCard, defaultStats, bullets } from '../common/TrustedPartnerSectionContent'


interface TrustedPartnerSectionProps {
    quote?: string
    stats?: StatCard[]
}

export function TrustedPartnerSection({ quote, stats = defaultStats }: TrustedPartnerSectionProps) {
    return (
        <Box sx={{ ...SECTION_LIGHT }}>
            <Container maxWidth="lg">
                <Grid container spacing={6} alignItems="center">
                    <Grid item xs={12} md={5}>
                        <Typography variant="h4" sx={{ color: COLOR_PRIMARY, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', mb: 2 }}>
                            Your Trusted Partner
                        </Typography>
                        <Typography variant="h3" sx={{ mb: 3 }}>
                            Built by people who've been in your shoes
                        </Typography>
                        {quote && (
                            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, mb: 1 }}>
                                "{quote}"
                            </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8, mb: 4 }}>
                            Contract Finder Pro was built by sales professionals with decades of UK Government experience. We know what it takes to find, qualify and win public sector work because we've done it.
                        </Typography>
                        <Stack spacing={2}>
                            {bullets.map(point => (
                                <Box key={point} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                    <CheckCircleIcon sx={{ fontSize: CHECKCIRCLE_SIZE, color: COLOR_PRIMARY, mt: 0.2, flexShrink: 0 }} />
                                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>{point}</Typography>
                                </Box>
                            ))}
                        </Stack>
                    </Grid>
                    <Grid item xs={12} md={7}>
                        <Grid container spacing={3}>
                            {stats.map(item => {
                                const cardSx = {
                                    background: 'rgba(255,255,255,0.55)',
                                    backdropFilter: 'blur(12px)',
                                    WebkitBackdropFilter: 'blur(12px)',
                                    border: '1px solid rgba(255,255,255,0.85)',
                                    borderTop: `3px solid ${item.color}`,
                                    borderRadius: 2,
                                    p: 3,
                                    height: '100%',
                                    boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                                }
                                const inner = (
                                    <>
                                        <Typography sx={{ color: item.color, fontWeight: 800, fontSize: '28px', lineHeight: 1, mb: 0.75 }}>
                                            {item.stat}
                                        </Typography>
                                        <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                                            {item.label}
                                            {item.href && <OpenInNewIcon sx={{ fontSize: 13, verticalAlign: 'text-bottom', ml: 0.5, color: 'text.secondary' }} />}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>{item.detail}</Typography>
                                    </>
                                )
                                return (
                                    <Grid item xs={12} sm={6} key={item.label}>
                                        {item.href ? (
                                            <Box
                                                component="a"
                                                href={item.href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{
                                                    ...cardSx,
                                                    display: 'block', textDecoration: 'none', color: 'inherit',
                                                    transition: 'box-shadow 0.2s',
                                                    '&:hover': { boxShadow: '0 6px 28px rgba(0,0,0,0.14)' },
                                                }}
                                            >
                                                {inner}
                                            </Box>
                                        ) : (
                                            <Box sx={cardSx}>{inner}</Box>
                                        )}
                                    </Grid>
                                )
                            })}
                        </Grid>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    )
}
